import { Save } from 'lucide-react';

export default function VintageLinkManagement({ 
  formData, 
  setFormData, 
  handleSave, 
  showToast 
}) {
  return (
    <div className="w-full h-full pt-20 pb-6 px-6">
      <div 
        className="w-full h-full rounded-2xl overflow-hidden flex"
        style={{
          background: 'linear-gradient(135deg, #FFFAEB 0%, #FFF8E1 100%)',
          boxShadow: `
            0 20px 60px rgba(16, 86, 82, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.5)
          `,
          border: '3px solid #105652'
        }}
      >
        {/* Left Side - Header & Buttons (30%) */}
        <div className="w-[30%] p-6 flex flex-col justify-center border-r-2 border-[#105652]">
          {/* Vintage Header with Ornamental Border */}
          <div className="text-center relative mb-6">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#105652] to-transparent"></div>
            <div className="py-4">
              <h2 className="text-2xl font-serif text-[#105652] mb-1" style={{ 
                textShadow: '2px 2px 4px rgba(16, 86, 82, 0.3)',
                fontFamily: 'Georgia, serif',
                letterSpacing: '0.05em'
              }}>
                Custom Link Management
              </h2>
              <p className="text-xs text-[#1E8479] italic" style={{ fontFamily: 'Georgia, serif' }}>
                Craft your unique project address
              </p>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#105652] to-transparent"></div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2.5">
            <button
              onClick={handleSave}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white transition-all duration-300 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #4C1D95 0%, #3d1777 100%)',
                boxShadow: '0 4px 15px rgba(76,29,149,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                border: '1px solid #3d1777'
              }}
            >
              <Save size={16} />
              <span className="font-serif text-sm">Save Changes</span>
            </button>
          </div>

          {/* Vintage Footer Note */}
          <div className="mt-6 text-center">
            <p className="text-xs text-[#1E8479] italic leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              Crafted with care for your distinguished portfolio
            </p>
          </div>
        </div>

        {/* Right Side - Form Fields (70%) */}
        <div className="w-[70%] p-6 flex items-center justify-center">
          {/* Vintage Paper Card */}
          <div 
            className="rounded-xl p-5 space-y-4 w-full"
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFFAEB 100%)',
              boxShadow: `
                0 8px 32px rgba(16, 86, 82, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.8),
                inset 0 -1px 0 rgba(16, 86, 82, 0.1)
              `,
              border: '2px solid #1E8479',
              position: 'relative'
            }}
          >
            {/* Corner Decorations */}
            <div className="absolute top-2 left-2 w-5 h-5 border-t-2 border-l-2 border-[#105652] opacity-30"></div>
            <div className="absolute top-2 right-2 w-5 h-5 border-t-2 border-r-2 border-[#105652] opacity-30"></div>
            <div className="absolute bottom-2 left-2 w-5 h-5 border-b-2 border-l-2 border-[#105652] opacity-30"></div>
            <div className="absolute bottom-2 right-2 w-5 h-5 border-b-2 border-r-2 border-[#105652] opacity-30"></div>

            <p className="text-xs text-[#105652] leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              Create a distinguished shareable URL for your project. This elegant address allows visitors to access your work as a standalone presentation.
            </p>

            {/* Prefix Toggle - Vintage Switch */}
            <div 
              className="p-3 rounded-lg"
              style={{
                background: 'linear-gradient(135deg, #FFFAEB 0%, #FFF8E1 100%)',
                border: '2px solid #1E8479',
                boxShadow: 'inset 0 2px 4px rgba(16,86,82,0.1)'
              }}
            >
              <label className="flex items-center gap-2.5 cursor-pointer">
                {/* Vintage Toggle Switch */}
                <div className="relative flex-shrink-0">
                  <input
                    type="checkbox"
                    id="useProjectPrefix"
                    checked={formData.useProjectPrefix}
                    onChange={(e) => setFormData(prev => ({ ...prev, useProjectPrefix: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div 
                    className="w-11 h-5 rounded-full transition-all duration-300"
                    style={{
                      background: formData.useProjectPrefix 
                        ? 'linear-gradient(135deg, #4C1D95 0%, #3d1777 100%)' 
                        : 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)',
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2), 0 1px 2px rgba(255,255,255,0.5)',
                      border: '2px solid #105652'
                    }}
                  >
                    <div 
                      className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-300"
                      style={{
                        background: 'linear-gradient(135deg, #FFFAEB 0%, #FFF8E1 100%)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.8)',
                        transform: formData.useProjectPrefix ? 'translateX(24px)' : 'translateX(0)',
                        border: '1px solid #1E8479'
                      }}
                    />
                  </div>
                </div>
                
                <div className="flex-1">
                  <span className="text-xs font-serif font-semibold text-[#105652] block mb-0.5">
                    Use "/project/" prefix
                  </span>
                  <p className="text-[10px] text-[#1E8479] leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                    Enabled: <code className="px-1 py-0.5 rounded bg-white/60 text-[10px] border border-[#1E8479]">/project/your-slug</code>
                    {' '} Disabled: <code className="px-1 py-0.5 rounded bg-white/60 text-[10px] border border-[#1E8479]">/your-slug</code>
                  </p>
                </div>
              </label>
            </div>

            {/* Custom Slug Input - Vintage Style */}
            <div>
              <label className="block text-xs font-serif font-semibold text-[#105652] mb-1.5">
                Custom Slug <span className="text-[#1E8479]">*</span>
              </label>
              <input
                type="text"
                value={formData.customSlug}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase()
                    .replace(/[^a-z0-9-]+/g, '-')
                    .replace(/^-+|-+$/g, '');
                  setFormData(prev => ({ ...prev, customSlug: value }));
                }}
                placeholder="my-distinguished-project"
                required
                className="w-full px-3 py-2 rounded-lg border-2 outline-none transition-all duration-300 text-[#105652] font-serif text-xs"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #FFFAEB 100%)',
                  borderColor: '#1E8479',
                  boxShadow: 'inset 0 2px 4px rgba(16,86,82,0.1), 0 1px 2px rgba(255,255,255,0.8)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#105652';
                  e.target.style.boxShadow = 'inset 0 2px 4px rgba(16,86,82,0.15), 0 0 0 3px rgba(16,86,82,0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#1E8479';
                  e.target.style.boxShadow = 'inset 0 2px 4px rgba(16,86,82,0.1), 0 1px 2px rgba(255,255,255,0.8)';
                }}
              />
              <p className="text-[10px] text-[#1E8479] mt-1 italic" style={{ fontFamily: 'Georgia, serif' }}>
                Only lowercase letters, numbers, and hyphens permitted
              </p>
            </div>

            {/* Auto-generate Button - Vintage Style */}
            <div>
              <label className="block text-xs font-serif font-semibold text-[#105652] mb-1.5">
                Generate from Title
              </label>
              <button
                type="button"
                onClick={() => {
                  const slug = formData.title
                    .toLowerCase()
                    .replace(/[^a-z0-9-]+/g, '-')
                    .replace(/^-+|-+$/g, '');
                  setFormData(prev => ({ ...prev, customSlug: slug }));
                  showToast('Slug generated from title', 'success');
                }}
                disabled={!formData.title}
                className="w-full px-4 py-2 rounded-lg text-white font-serif text-xs transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: formData.title 
                    ? 'linear-gradient(135deg, #4C1D95 0%, #3d1777 100%)' 
                    : 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)',
                  boxShadow: '0 4px 12px rgba(76,29,149,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                  border: '1px solid #3d1777'
                }}
              >
                Generate Elegant Slug
              </button>
            </div>

            {/* Preview URL - Vintage Display */}
            {formData.customSlug && (
              <div 
                className="p-3 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, #d4f4dd 0%, #c4e7d4 100%)',
                  border: '2px solid #1E8479',
                  boxShadow: 'inset 0 2px 4px rgba(16,86,82,0.1), 0 2px 8px rgba(16,86,82,0.2)'
                }}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-serif font-semibold text-[#105652]">
                    Your Distinguished URL
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const url = `${window.location.origin}${formData.useProjectPrefix ? '/project/' : '/'}${formData.customSlug}`;
                      navigator.clipboard.writeText(url);
                      showToast('URL copied to clipboard!', 'success');
                    }}
                    className="px-2.5 py-1 rounded-lg text-white font-serif text-[10px] transition-all duration-300 hover:scale-105 flex items-center gap-1"
                    style={{
                      background: 'linear-gradient(135deg, #1E8479 0%, #105652 100%)',
                      boxShadow: '0 2px 8px rgba(16,86,82,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                      border: '1px solid #105652'
                    }}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </button>
                </div>
                <div 
                  className="px-2.5 py-1.5 rounded-lg font-mono text-[10px] text-[#105652] break-all"
                  style={{
                    background: 'rgba(255,255,255,0.6)',
                    border: '1px solid #1E8479',
                    boxShadow: 'inset 0 1px 3px rgba(16,86,82,0.1)'
                  }}
                >
                  {window.location.origin}{formData.useProjectPrefix ? '/project/' : '/'}{formData.customSlug}
                </div>
                <p className="text-[10px] text-[#105652] mt-1 italic flex items-center gap-1" style={{ fontFamily: 'Georgia, serif' }}>
                  <span>✓</span> Ready to share with the world
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
